import type React from 'react'

/**
 * Below the `md` breakpoint the panel spans the column between the two 3rem icon rails, less its
 * 0.5rem side margins and 2rem of padding — but the controls inside are sized from fixed tokens
 * (`--panel-dropdown-width` 260px, `--panel-single-photo-width` 248px). On a ~375px phone that
 * leaves ~231px, so the Category / Filters / Sort controls and single-column cards would run past
 * the panel's edge. Cap both tokens to the width actually available; on wider narrow viewports the
 * panel is wider than either token, so the 260px / 248px values still apply.
 */
const NARROW_PANEL_WIDTH_STYLE = {
  '--panel-dropdown-width': 'min(260px, calc(100vw - 9rem))',
  '--panel-single-photo-width': 'min(248px, calc(100vw - 9rem))',
} as React.CSSProperties

interface StackedPanelProps {
  title: string
  children: React.ReactNode
}

/**
 * The right panel on narrow viewports: stacked under the spread area instead of beside it, with
 * its own rounded corners and border like the spread area above it (WMPCM-11050 AC-1). `RightPanel`
 * portals it into the host's stack slot below the canvas.
 */
export function StackedPanel({ title, children }: StackedPanelProps) {
  return (
    <section
      aria-label={title}
      data-right-panel-stacked="true"
      className="mx-2 mb-2 flex h-[45%] min-h-0 shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-background"
    >
      <div className="flex h-12 shrink-0 items-center border-b border-border px-4">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-4"
        style={NARROW_PANEL_WIDTH_STYLE}
      >
        {children}
      </div>
    </section>
  )
}
